import React, { useEffect, useState } from 'react';
import { api, mensajeError } from '../../../lib/api';
import { Loader2 } from 'lucide-react';
import {
  COLOR_BARRA, ORDEN_CLASIFICACION, Pildoras, comoObjeto, duracion, etiqueta, euros, numero,
} from './comun';

/**
 * Métricas del bot: leads atendidos, primera respuesta, reparto por
 * clasificación, traspasos, citas y coste por lead (o tokens si no hay
 * precios puestos). Solo cuenta lo enviado de verdad (modo «Enviando»); las
 * llamadas de borradores, simulador y pruebas salen aparte.
 */

const PERIODOS = [['7', '7 días'], ['30', '30 días'], ['90', '90 días']];
const MODOS = [['envia', 'enviando'], ['prueba', 'borradores'], ['simulador', 'simulador'], ['pruebas', 'pruebas automáticas']];

function Cifra({ etiqueta: titulo, valor, pista, testid }) {
  return (
    <div className="rounded-lg border p-3 min-w-0" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">{titulo}</div>
      <div className="text-2xl font-display font-semibold text-[#13231E] mt-0.5 tabular-nums break-words" data-testid={`${testid}-valor`}>
        {valor}
      </div>
      {pista ? <div className="text-xs text-muted-foreground mt-0.5">{pista}</div> : null}
    </div>
  );
}

function porcentaje(parte, total) {
  return total ? Math.round((parte / total) * 100) : 0;
}

export default function MetricasBot() {
  const [dias, setDias] = useState('30');
  const [datos, setDatos] = useState(null);
  const [fallo, setFallo] = useState('');
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    let vivo = true;
    setCargando(true);
    api.get('/api/whatsapp/bot/metricas', { params: { dias: Number(dias) } })
      .then((r) => { if (vivo) { setDatos(comoObjeto(r.data)); setFallo(''); } })
      .catch((e) => { if (vivo) setFallo(mensajeError(e, 'No se han podido leer las métricas')); })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [dias]);

  const d = datos || {};
  const leads = Number(d.leads_atendidos || 0);
  const reparto = comoObjeto(d.reparto);
  const totalReparto = ORDEN_CLASIFICACION.reduce((s, k) => s + Number(reparto[k] || 0), 0);
  const tokens = comoObjeto(d.tokens);
  const totalTokens = ['entrada', 'salida', 'cache_leidos', 'cache_creados'].reduce((s, k) => s + Number(tokens[k] || 0), 0);
  const porModo = comoObjeto(d.por_modo);
  const conPrecio = d.coste_por_lead_eur !== null && d.coste_por_lead_eur !== undefined;

  let coste;
  let pistaCoste;
  if (conPrecio) {
    coste = euros(d.coste_por_lead_eur);
    pistaCoste = d.coste_total_eur !== null && d.coste_total_eur !== undefined ? `${euros(d.coste_total_eur)} en total` : '';
  } else {
    coste = leads ? `${numero(Math.round(totalTokens / leads))} tokens` : '—';
    pistaCoste = 'Sin precios: ponlos en Ajustes para verlo en euros.';
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Pildoras etiqueta="Periodo" opciones={PERIODOS} valor={dias} onCambio={setDias} testid="bot-metricas-dias" />
        {cargando ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" aria-label="Cargando" /> : null}
      </div>
      {fallo ? (
        <div className="text-sm text-red-700 border border-red-200 bg-red-50 rounded p-3" data-testid="bot-metricas-fallo">{fallo}</div>
      ) : null}
      {datos ? (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5" data-testid="bot-metricas">
            <Cifra etiqueta="Leads atendidos" valor={numero(leads)} pista="Con alguna respuesta del bot enviada" testid="bot-metrica-leads" />
            <Cifra etiqueta="Primera respuesta" valor={duracion(d.primera_respuesta_mediana_s)}
              pista={d.primera_respuesta_media_s !== null && d.primera_respuesta_media_s !== undefined
                ? `Mediana · media ${duracion(d.primera_respuesta_media_s)}` : 'Mediana'}
              testid="bot-metrica-primera" />
            <Cifra etiqueta="Traspasos al equipo" valor={numero(d.traspasos)}
              pista={leads ? `${porcentaje(Number(d.traspasos || 0), leads)} % de los atendidos` : undefined} testid="bot-metrica-traspasos" />
            <Cifra etiqueta="Citas conseguidas" valor={numero(d.citas)} pista="Leads que acaban citados tras hablar con el bot"
              testid="bot-metrica-citas" />
            <Cifra etiqueta={conPrecio ? 'Coste por lead' : 'Tokens por lead'} valor={coste} pista={pistaCoste} testid="bot-metrica-coste" />
          </div>

          <div className="space-y-1.5" data-testid="bot-metricas-reparto">
            <div className="text-sm font-medium text-[#13231E]">Cómo los ha clasificado</div>
            {totalReparto ? (
              <>
                <div className="flex h-3 w-full overflow-hidden rounded-full bg-muted" role="img"
                  aria-label={ORDEN_CLASIFICACION.map((k) => `${etiqueta('clasificacion', k)}: ${Number(reparto[k] || 0)}`).join(', ')}>
                  {ORDEN_CLASIFICACION.filter((k) => Number(reparto[k] || 0) > 0).map((k) => (
                    <div key={k} className={`${COLOR_BARRA[k]} h-full`} style={{ width: `${(Number(reparto[k]) / totalReparto) * 100}%` }}
                      title={`${etiqueta('clasificacion', k)}: ${reparto[k]}`} data-testid={`bot-reparto-${k}`} />
                  ))}
                </div>
                <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                  {ORDEN_CLASIFICACION.map((k) => (
                    <li key={k} className="flex items-center gap-1.5" data-testid={`bot-reparto-leyenda-${k}`}>
                      <span className={`inline-block h-2.5 w-2.5 rounded-sm ${COLOR_BARRA[k]}`} aria-hidden="true" />
                      {etiqueta('clasificacion', k)}
                      <span className="tabular-nums font-semibold">{numero(reparto[k])}</span>
                      <span className="text-muted-foreground tabular-nums">({porcentaje(Number(reparto[k] || 0), totalReparto)} %)</span>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Todavía no hay leads clasificados en este periodo.</p>
            )}
          </div>

          <div className="text-xs text-muted-foreground space-y-0.5 border-t pt-3" data-testid="bot-metricas-uso">
            <div>
              Llamadas a Claude: <span className="font-semibold text-[#13231E] tabular-nums">{numero(d.llamadas)}</span>
              {Object.keys(porModo).length ? (
                <> ({MODOS.filter(([k]) => porModo[k] !== undefined).map(([k, n]) => `${n} ${numero(porModo[k])}`).join(' · ')})</>
              ) : null}
            </div>
            <div className="tabular-nums">
              Tokens: {numero(tokens.entrada)} de entrada · {numero(tokens.cache_leidos)} leídos de caché ·{' '}
              {numero(tokens.cache_creados)} escritos en caché · {numero(tokens.salida)} de salida
              {d.coste_total_eur !== null && d.coste_total_eur !== undefined ? ` · coste total ${euros(d.coste_total_eur)}` : ''}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
